import React, { useCallback, useMemo, useEffect } from 'react';

import Button from 'components/Button';
import MultiSelectInput, {
    KeyExtractor,
    ValueExtractor,
    MultiSelectInputChangeCallback,
    RenderControlCallback
} from '@ra/components/Form/MultiSelectInput';

import cs from '@ra/cs';
import { Localize, _ } from 'services/i18n';
import { getPriorityIndicators } from 'services/bootstrap';

import { useAppSelector } from 'hooks/store';
import { useFilters, useFiltersDispatch } from 'hooks/filters';
import { selectPriorityAreas } from 'store/selectors/priorityIndicator';

import type { PriorityIndicator } from 'services/types';

import styles from './styles.scss';

const keyExtractor: KeyExtractor<PriorityIndicator> = (item) => item.id;
const valueExtractor: ValueExtractor<PriorityIndicator, string> = (item) => item.title;

interface FilterOrganizationsProps {
    filterType: string;
    isClearable?: boolean;
}

const FilterOrganizations: React.FC<FilterOrganizationsProps> = (props) => {
    const { filterType } = props;

    const dispatch = useFiltersDispatch();
    const filters = useFilters();

    const priorityAreas = useAppSelector((state) => selectPriorityAreas(state));

    const priorityIndicatorsFilter = useMemo(() => {
        return filters.find((el) => el.filterType === filterType);
    }, [filters, filterType]);

    useEffect(() => {
        if (!priorityAreas.length) {
            getPriorityIndicators();
        }
    }, []);

    const handlePriorityAreaChange: MultiSelectInputChangeCallback<PriorityIndicator> = useCallback(
        ({ value: option }) => {
            if (option?.length) {
                return dispatch({
                    type: 'add',
                    filter: {
                        filterType,
                        filterValue: option,
                        query: {
                            outputs__activities__priority_indicators__in: option
                                .map((opt) => opt.id)
                                .toString()
                        }
                    }
                });
            }
            dispatch({
                type: 'remove',
                filter: { filterType }
            });
        },
        [dispatch, filterType]
    );

    const renderControl: RenderControlCallback<PriorityIndicator, string> = useCallback(
        ({ selectedItems, handleCaretClick }) => {
            return (
                <Button
                    small
                    tertiary
                    leftIcon="settings_suggest"
                    rightIcon="expand_more"
                    onClick={handleCaretClick}
                    className={styles.controlButton}
                    iconClassName={styles.controlButtonIcon}
                >
                    {selectedItems.length ? (
                        <>
                            {selectedItems.length === 1
                                ? selectedItems[0].title.length > 50
                                    ? selectedItems[0].title.slice(0, 50) + '...'
                                    : selectedItems[0].title
                                : `${selectedItems.length} ${_('themes')}`}
                        </>
                    ) : (
                        <Localize>Theme(s)</Localize>
                    )}
                </Button>
            );
        },
        []
    );

    return (
        <div className={styles.container}>
            <MultiSelectInput
                className={styles.select}
                optionsWrapperClassName={styles.optionsWrapper}
                selectOptionClassName={styles.selectOption}
                controlClassName={cs(styles.selectControl, {
                    [styles.selectControlFilled]: !!priorityIndicatorsFilter
                })}
                defaultValue={priorityIndicatorsFilter?.filterValue || []}
                searchable
                clearable
                renderControl={renderControl}
                options={priorityAreas}
                keyExtractor={keyExtractor}
                valueExtractor={valueExtractor}
                onChange={handlePriorityAreaChange}
                placeholder={_('Organization')}
                anchorOrigin="bottom right"
                transformOrigin="top right"
            />
        </div>
    );
};

export default FilterOrganizations;
